import { useMutation } from "@tanstack/react-query";
import { apiClient } from "./client";

/**
 * Phase 1 Auth API (backend.md §24.5).
 *
 * Client-side SRP still goes through Amplify for the main login path;
 * these endpoints cover the NEW_PASSWORD_REQUIRED / SOFTWARE_TOKEN_MFA
 * challenges (first-login + MFA-at-signin) and the forgot-password flow.
 * The forgot-password endpoints are intentionally vague about account
 * existence — UI copy must not leak enumeration signals either.
 */

export type ChallengeName = "NEW_PASSWORD_REQUIRED" | "SOFTWARE_TOKEN_MFA";

export interface ChallengeRequest {
  session: string;
  challenge_name: ChallengeName;
  username: string;
  new_password?: string;
  mfa_code?: string;
}

export interface ChallengeResponse {
  id_token?: string | null;
  access_token?: string | null;
  refresh_token?: string | null;
  expires_in?: number | null;
  challenge_name?: ChallengeName | null;
  session?: string | null;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ConfirmForgotPasswordRequest {
  email: string;
  code: string;
  new_password: string;
}

export interface GenericMessage {
  message: string;
}

export function useRespondToChallenge() {
  return useMutation<ChallengeResponse, Error, ChallengeRequest>({
    mutationFn: async (body) =>
      (await apiClient.post<ChallengeResponse>("/auth/challenge", body)).data,
  });
}

export function useForgotPassword() {
  return useMutation<GenericMessage, Error, ForgotPasswordRequest>({
    mutationFn: async (body) =>
      (await apiClient.post<GenericMessage>("/auth/forgot-password", body)).data,
  });
}

export function useConfirmForgotPassword() {
  return useMutation<GenericMessage, Error, ConfirmForgotPasswordRequest>({
    mutationFn: async (body) =>
      (await apiClient.post<GenericMessage>("/auth/confirm-forgot-password", body)).data,
  });
}

/**
 * Invitation-acceptance — the invitee submits their preferred initial
 * password along with the opaque token from the email link. Backend
 * validates the token, sets the Cognito password with Permanent=true,
 * and marks the invitation accepted. No OTP round-trip, no challenge
 * flow. UI then routes the invitee to LoginPage with their email
 * pre-filled.
 *
 * Deliberately calls raw `fetch` (not apiClient) to skip the bearer
 * interceptor — the endpoint is public and an unauthed caller should
 * not accidentally hit a 401.
 */
export interface AcceptInviteRequest {
  token: string;
  new_password: string;
}

export interface AcceptInviteResponse {
  email: string;
  message: string;
}

export function useAcceptInvite() {
  return useMutation<AcceptInviteResponse, Error, AcceptInviteRequest>({
    mutationFn: async (body) => {
      const base = apiClient.defaults.baseURL ?? "";
      const res = await fetch(`${base}/auth/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `accept-invite failed: ${res.status}`);
      }
      return (await res.json()) as AcceptInviteResponse;
    },
  });
}

/**
 * Cognito password policy mirrored from monitoring-infra's Cognito user
 * pool config (modules/cognito/main.tf): min 12 chars, upper + lower +
 * digit + symbol. Exposed so the first-login + reset screens can render
 * a checklist without duplicating the rules.
 */
export const PASSWORD_POLICY = {
  minLength: 12,
  requireLowercase: true,
  requireUppercase: true,
  requireNumbers: true,
  requireSymbols: true,
} as const;

export interface PasswordCheck {
  label: string;
  satisfied: boolean;
}

export function evaluatePassword(pw: string): PasswordCheck[] {
  return [
    { label: `At least ${PASSWORD_POLICY.minLength} characters`, satisfied: pw.length >= PASSWORD_POLICY.minLength },
    { label: "Lowercase letter", satisfied: /[a-z]/.test(pw) },
    { label: "Uppercase letter", satisfied: /[A-Z]/.test(pw) },
    { label: "Number", satisfied: /[0-9]/.test(pw) },
    { label: "Symbol", satisfied: /[^A-Za-z0-9]/.test(pw) },
  ];
}
